<template>
  <div class="centered-container">
    <v-row align-center class="centered-container" justify="center">
      <v-card id="codeInsertion" class="elevation-3">
        <v-card-text>
          <h3 class="title">{{ $t("CODE_WELCOME") }}</h3>
          <p class="codeNotice">{{ $t("CODE_NOTICE") }}</p>
        </v-card-text>
        <v-card-text>
          <div
              v-for="(code, it) in codes"
              :key="it + '_' + codeImportCounter"
              class="codeContainer"
          >
            <v-text-field
                ref="codeField"
                v-model="codes[it]"
                :class="{codeInput: true, deletable: true}"
                :disabled="isCodeFromStore(code)"
                :error="errors[it] != null"
                :error-messages="errors[it]"
                :label="$t('CODE_LABEL')"
                :success="valid[it]"
                name="code"
                type="text"
                @input="onCodeChanged(it)"
            ></v-text-field>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-icon
                    v-show="it > 0 || codes.length > 1"
                    class="hoverPointer"
                    @click="removeCode(it)"
                    v-on="on"
                >close
                </v-icon>
                <v-icon
                    v-show="it === 0 && codes.length === 1 && codes[it] !== ''"
                    class="hoverPointer"
                    @click="clearCode(it)"
                    v-on="on"
                >close
                </v-icon>
              </template>
              <span>{{ $t("REMOVE_CODE") }}</span>
            </v-tooltip>
          </div>

          <div class="addCode" @click="addCode">

          </div>
        </v-card-text>
        <v-card-actions>
          <v-row align-center class="codeButtons">
            <v-col class="buttonContainer">
              <v-btn :disabled="!mayAddCode" @click="addCode">
                <v-icon>add</v-icon>
                {{ $t("BUTTON_OTHER_CODE") }}
              </v-btn>
            </v-col>
            <v-col class="buttonContainer">
              <v-btn
                  v-if="this.committedCodes.length > 0 && this.codesInputMatchesSelection"
                  :disabled="! allCodesValid"
                  color="primary"
                  @click="continueToNextStep()"
              >
                {{ $t("BUTTON_SUBMIT_CODE_UNMODIFIED") }}
              </v-btn>
              <div v-else>
                <Altcha :key="altchaKey" v-model="altcha"/>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <div v-on="on">
                      <v-btn
                          v-if="committedCodes.length > 0 && !codesInputMatchesSelection"
                          :disabled="! maySubmit"
                          color="primary"
                          @click="start"
                      >
                        {{ $t("BUTTON_SUBMIT_CODE_MODIFIED") }}
                      </v-btn>
                      <v-btn
                          v-else
                          :disabled="! maySubmit"
                          color="primary"
                          @click="start"
                      >
                        {{ $t("BUTTON_SUBMIT_CODE") }}
                      </v-btn>
                    </div>
                  </template>
                  <span v-if="buttonTooltip">{{ buttonTooltip }}</span>
                </v-tooltip>
              </div>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-row>
  </div>
</template>

<script lang="ts">
import Component from "vue-class-component";
import {CodeApi, ImageApi, ImageCollectionDto} from "@swagger";
import BasicComponent from "@/global/BasicComponent";
import {Watch} from "vue-property-decorator";
import Altcha from "@/components/Altcha.vue";

@Component({
  components: {
    Altcha,
  }
})
export default class CodeSubmission extends BasicComponent {

  codes: string[] = [""];
  loading: boolean = false;
  errorMessage: string | null = null;
  valid: boolean[] = [];
  errors: string[] = [];
  codeImportCounter: number = 0; // Whenever the codes are imported from the URL query parameters, this counter is increased so that the for loop in the template updates reactively.

  altcha: string = null;
  altchaKey: number = 0; // Increase to rerender

  get mayAddCode() {
    return this.codeValid(this.codes[this.codes.length - 1]);
  }

  /**
   * Ignores empty codes, but needs at least one non-empty value
   */
  get allCodesValid() {
    const codes = this.codes;
    const isValid = (it, index) => {
      return (this.codeValid(it) && !this.errors[index]);
    }
    if (!codes.every((it, index) => {
      return !this.hasText(it) || isValid(it, index);
    })) {
      return false;
    }
    return codes.filter(isValid).length > 0;
  }

  get maySubmit() {
    return this.allCodesValid && this.altchaKeyValid;
  }

  get buttonTooltip() {
    if (this.maySubmit) {
      return this.$t("BUTTON_SUBMIT_CODE");
    }
    if (this.allCodesValid && !this.altchaKeyValid) {
      return this.$t("CAPTCHA_PENDING_TOOLTIP")
    }
    return this.errors.find(it => it != null);
  }

  get altchaKeyValid() {
    return this.altcha != null && this.altcha.length > 0;
  }

  created() {
  }

  mounted() {
    if (this.altchaKey) this.altchaKey++;
    if (this.$store.getters.step === "1") {
      this.trackPage("Code-Eingabe");
    }

    this.$nextTick(() => {
      if (this.$store.getters.step == 1) {
        this.addCodesFromStore([]) // add from store onMounted so hot-reload works

        if (this.$route.query.codes) {
          const routeCodes = String(this.$route.query.codes).split(",")
          this.addCodes(routeCodes)
        }

        if (this.committedCodes.length > 0) {
          if (!this.codes.some(code => !this.hasText(code))) {
            this.addCode();
          }
        }
      }
    });
  }

  @Watch('committedCodes', {immediate: true, deep: true})
  addCodesFromStore(committedCodes: string[]) {
    // NOTE: the store isn't initialized at the start, so we can only listen to its change (instead of onMounted)
    //       but the committedCodes get set by App::created
    const unincludedCodes = (committedCodes ?? []).filter(code => !this.codes.includes(code))
    this.addCodes(unincludedCodes, 'prepend')
  }

  addCodes(codes: string[], operation: 'prepend' | 'append' = 'append') {
    if (codes.length === 0) {
      return
    }

    const lastItem = this.codes[this.codes.length - 1]
    if (lastItem === "") {
      this.codes.pop()
    }

    if (operation === 'prepend') {
      this.codes.unshift(...codes)
    } else {
      this.codes.push(...codes)
    }

    this.codeImportCounter++;
    for (let i = 0; i < this.codes.length; i++) {
      this.onCodeChanged(i);
    }
  }

  isCodeFromStore(code: string) {
    return this.committedCodes.includes(code)
  }

  addCode() {
    this.codes.push("");
    this.errors.push(null);
    this.valid.push(null);
    this.trackEvent("Code", "Hinzufügen");
  }

  removeCode(id) {
    this.codes.splice(id, 1);
    this.$store.commit("setCodesInput", this.codes);
    this.errors.splice(id, 1);
    this.valid.splice(id, 1);
  }

  clearCode(id) {
    this.$set(this.codes, id, "");
    this.onCodeChanged(id);
  }

  hasText(str: string): boolean {
    return str != null && str.trim().length > 0;
  }

  codeValid(code) {
    return code.length > 0;
  }

  onCodeChanged(id) {
    let code = this.codes[id];
    code = code.split("-").join(""); // Remove '-'
    let newCode = "";
    for (let i = 0; i < code.length; i++) {
      const char = code.charAt(i);
      if (!"abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890".includes(String(char))) continue;
      newCode += char;
    }
    code = newCode;
    newCode = "";
    for (let i = 0; i < code.length; i++) {
      newCode += code.charAt(i);
      if (i % 3 == 2 && i < code.length - 1) {
        newCode += "-";
      }
    }

    this.$set(this.codes, id, newCode);
    this.$store.commit("setCodesInput", this.codes);

    this.errors[id] = null;
    this.valid[id] = null;
  }

  start() {
    this.errorMessage = null;

    this.trackEvent("Code", "Eingelöst");
    const codes = this.codes.filter(c => this.hasText(c));
    new CodeApi(this.apiConfiguration).redeemCodes({codes: codes}, this.altcha)
        .then((data: boolean[]) => {
          this.valid = data;
          if (data.every(it => it)) {
            this.errors = [];
            new ImageApi(this.apiConfiguration).getImages()
                .then((data: ImageCollectionDto) => {
                  this.$store.commit("setImageSelection", data);
                  this.continueToNextStep();
                  this.checkIfNewsletterAccepted((accepted) => {
                    if (!accepted) {
                      this.$store.commit("setNewsletterDialogOpen", true);
                    }
                  });
                  this.trackEvent("Code", "Eingelöst");
                })
            this.getBasketData(); // Items could have been removed due to code removal
          } else {
            this.errors = data.map(successful => {
              if (successful) return null;
              else return this.$t("CODE_INVALID").toString();
            })
            this.altchaKey++;
          }
        })
        .catch((err) => {
          this.altchaKey++;
          err.json().then(error => {
            this.errorMessage = error.message;
            console.log(error);
          });
        });
  }

  continueToNextStep() {
    this.removeCodesFromQueryParams();
    this.setStep(2);
    this.requestProducts();
    this.requestCoupons();
  }

};
</script>
<style lang="scss" scoped>

@use "../stylesheets/Variables.scss";

.centered-container {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  height: 100%;
  margin: 5px !important;

  .addCode {
    * {
      display: inline-block;
      color: rgba(117, 117, 117, 0.49)
    }

    &:hover {
      cursor: pointer;
    }

    .addCodeText {
      font-weight: 500;
      transform: translateY(-3px);
      text-decoration: underline;
      text-decoration-style: dotted;
      text-underline-width: 2px;
    }
  }

  .codeNotice {
    text-align: center;
  }

  #codeInsertion {
    padding: 40px;
    max-width: 400px;
  }

  .title {
    text-align: center;
    margin-bottom: 30px;
  }

  .codeContainer {
    * {
      display: inline-block;
    }

    .codeInput {
      width: 100%;

      &.deletable {
        width: calc(100% - 24px);
      }
    }
  }

  .codeButtons {
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;

    .buttonContainer {
      display: inline-flex;
      justify-content: center;
      padding-bottom: 10px;
    }
  }
}

</style>
<style lang="scss">
.grecaptcha-badge {
  position: absolute;
  bottom: 0;
}

.codeInput {
  input {
    text-transform: uppercase;
  }
}
</style>
